import { useEffect, useState } from 'react';
import { ouvreDocument } from '../../api/documents';
import { portailApi } from '../../api/resources';
import { fmt } from '../../lib/currency';
import { ETATS_COMMANDE } from '../../lib/etats-commande';
import { tr, trf } from '../../lib/i18n';
import { C } from '../../theme/palette';
import { libelleChantier } from './listes';
import { alerte, bouton, carte, champ, dateCourte, jourEtHeure, nombre } from './styles';

/** La clé d'un chantier, pour le filtre : son numéro et son nom. */
const cleChantier = (c) => (c.chantierNumero || '') + '|' + (c.chantier || '').trim();

/**
 * « 📦 Toutes mes commandes », chez tous les fournisseurs : pour chacune, le
 * magasin, le bon de commande, le chantier et son numéro, l'état, le total
 * (à qui voit les prix), qui l'a passée et qui l'a validée. Filtres par
 * chantier et par fournisseur ; le total commandé d'un chantier, pour le
 * responsable et les chefs. Le bon de commande A4 s'ouvre chez n'importe quel
 * fournisseur — « à valider » compris, marqué comme tel —, jamais pour
 * l'ouvrier.
 *
 * @param {object} props propriétés
 * @param {string} [props.client] entreprise cliente
 * @param {boolean} props.voitLesPrix montre les montants et le total d'un chantier
 * @param {(commande: object) => void} props.recommande remet au panier les articles
 *   d'une commande du magasin ouvert
 * @returns {import('react').ReactElement} la liste
 */
export function ToutesMesCommandes({ client, voitLesPrix, recommande }) {
  const [liste, setListe] = useState(null);
  const [chantier, setChantier] = useState('');
  const [fournisseur, setFournisseur] = useState('');
  const [ouverte, setOuverte] = useState(null);
  const [erreurPdf, setErreurPdf] = useState({});
  useEffect(() => {
    let annule = false;
    portailApi
      .toutesLesCommandes()
      .then((l) => !annule && setListe(Array.isArray(l) ? l : []))
      .catch(() => !annule && setListe([]));
    return () => {
      annule = true;
    };
  }, []);
  if (liste === null) return <div style={{ ...carte, color: C.muted }}>Chargement…</div>;

  const chantiers = [];
  const fournisseurs = [];
  liste.forEach((x) => {
    const c = x.commande || {};
    const cle = cleChantier(c);
    if ((c.chantier || c.chantierNumero) && !chantiers.some(([k]) => k === cle))
      chantiers.push([cle, libelleChantier(c.chantierNumero, c.chantier)]);
    if (!fournisseurs.some(([id]) => id === String(x.magasinId)))
      fournisseurs.push([String(x.magasinId), x.magasin]);
  });
  chantiers.sort((a, b) => a[1].localeCompare(b[1]));
  const visibles = liste.filter(
    (x) =>
      (!chantier || cleChantier(x.commande || {}) === chantier) &&
      (!fournisseur || String(x.magasinId) === fournisseur),
  );
  const totaux = visibles.filter(
    (x) => x.commande?.total != null && !['REFUSEE', 'ANNULEE'].includes(x.commande.statut),
  );
  const totalChantier = totaux.reduce((s, x) => s + nombre(x.commande.total), 0);
  const devise = visibles[0]?.devise || 'CHF';

  const pdf = async (x) => {
    const cle = x.magasinId + ':' + x.commande.id;
    setErreurPdf((p) => ({ ...p, [cle]: '' }));
    try {
      await ouvreDocument(portailApi.bonDeCommandeChez(x.magasinId, x.commande.id));
    } catch (e) {
      if (!e?.dejaAffiche)
        setErreurPdf((p) => ({
          ...p,
          [cle]: e?.message || tr('Le bon de commande n’a pas pu s’ouvrir.'),
        }));
    }
  };

  return (
    <section aria-labelledby="titre-toutes">
      <h2 id="titre-toutes" style={{ fontSize: 18, margin: '4px 0 2px' }}>
        📦 Toutes mes commandes
      </h2>
      {client && (
        <div style={{ fontSize: 12, color: C.muted, marginBottom: 8 }}>
          {trf('{0} · tous les fournisseurs', client)}
        </div>
      )}
      {liste.length === 0 ? (
        <div style={{ ...carte, textAlign: 'center', color: C.muted }}>
          Aucune commande pour l’instant.
        </div>
      ) : (
        <div
          style={{
            // Côte à côte s'ils tiennent, l'un sous l'autre sur un petit
            // téléphone plutôt que « Tous les fournisseu… ».
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
            gap: 6,
            marginBottom: 10,
          }}
        >
          <select
            aria-label={tr('Chantier')}
            value={chantier}
            onChange={(e) => setChantier(e.target.value)}
            style={{ ...champ, fontSize: 13, minWidth: 0 }}
          >
            <option value="">Tous les chantiers</option>
            {chantiers.map(([cle, lib]) => (
              <option key={cle} value={cle}>
                {lib}
              </option>
            ))}
          </select>
          <select
            aria-label={tr('Fournisseur')}
            value={fournisseur}
            onChange={(e) => setFournisseur(e.target.value)}
            style={{ ...champ, fontSize: 13, minWidth: 0 }}
          >
            <option value="">Tous les fournisseurs</option>
            {fournisseurs.map(([id, nom]) => (
              <option key={id} value={id}>
                {nom}
              </option>
            ))}
          </select>
        </div>
      )}
      {visibles.map((x) => {
        const c = x.commande;
        const cle = x.magasinId + ':' + c.id;
        const etat = ETATS_COMMANDE[c.statut] || { texte: c.statut, couleur: C.grey };
        const ouvert = ouverte === cle;
        const lib = libelleChantier(c.chantierNumero, c.chantier);
        return (
          <div
            key={cle}
            data-testid={'commande-' + cle}
            style={{ ...carte, opacity: c.statut === 'REFUSEE' ? 0.85 : 1 }}
          >
            <button
              onClick={() => setOuverte(ouvert ? null : cle)}
              aria-expanded={ouvert}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                background: 'none',
                border: 'none',
                padding: 0,
                fontFamily: 'inherit',
                color: C.ink,
                cursor: 'pointer',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <b style={{ fontSize: 14, minWidth: 0, overflowWrap: 'anywhere' }}>
                  🏬 {x.magasin}
                </b>
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 800,
                    color: etat.couleur,
                    background: etat.couleur + '1A',
                    borderRadius: 8,
                    padding: '3px 7px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {etat.texte}
                </span>
              </div>
              <div style={{ fontSize: 13, marginTop: 4 }}>
                {tr('Bon de commande')} <b>{c.numero}</b>
              </div>
              {(lib || (voitLesPrix && c.total != null)) && (
                <div style={{ fontSize: 13, marginTop: 2 }}>
                  {lib && (
                    <>
                      🏗 <span translate="no">{lib}</span>
                    </>
                  )}
                  {voitLesPrix && c.total != null
                    ? (lib ? ' · ' : '') + x.devise + ' ' + fmt(c.total)
                    : ''}
                </div>
              )}
              <div style={{ fontSize: 12, color: C.grey, marginTop: 2 }}>
                {c.demandeur
                  ? trf('Commandée par {0} · {1}', c.demandeur, dateCourte(c.date))
                  : dateCourte(c.date)}
              </div>
              {c.statut === 'REFUSEE' ? (
                <div style={{ fontSize: 12.5, color: C.red, marginTop: 2, fontWeight: 700 }}>
                  {c.motifRefus ? '« ' + c.motifRefus + ' »' : ''}
                  {c.validePar ? ' · ' + c.validePar : ''}
                </div>
              ) : (
                c.validePar && (
                  <div style={{ fontSize: 12, color: C.grey, marginTop: 2 }}>
                    {trf('Validée par {0}, le {1}', c.validePar, jourEtHeure(c.valideLe))}
                  </div>
                )
              )}
            </button>
            {x.bonDeCommande && (
              <button
                onClick={() => pdf(x)}
                style={{
                  ...bouton(C.navy, false),
                  padding: '7px 10px',
                  minHeight: 40,
                  fontSize: 12,
                  marginTop: 8,
                }}
              >
                📄 Bon de commande A4
              </button>
            )}
            {erreurPdf[cle] && (
              <div role="alert" style={alerte}>
                {erreurPdf[cle]}
              </div>
            )}
            {ouvert && (
              <div style={{ marginTop: 8, borderTop: '1px solid ' + C.border, paddingTop: 8 }}>
                {c.adresseLivraison && (
                  <div style={{ fontSize: 12, color: C.grey, marginBottom: 4 }} translate="no">
                    📍 {c.adresseLivraison}
                  </div>
                )}
                {(c.lignes || []).map((l, i) => (
                  <div
                    key={i}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 8,
                      fontSize: 13,
                      padding: '3px 0',
                    }}
                  >
                    <span>
                      {nombre(l.quantite)} {l.unite} · {l.designation}
                    </span>
                    {voitLesPrix && l.prixNet != null && (
                      <span>{fmt(nombre(l.prixNet) * nombre(l.quantite))}</span>
                    )}
                  </div>
                ))}
                {x.courant && (
                  <button
                    onClick={() => recommande(c)}
                    style={{ ...bouton(C.teal), width: '100%', marginTop: 8 }}
                  >
                    🔁 Recommander les mêmes articles
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}
      {voitLesPrix && chantier && totaux.length > 0 && (
        <div
          data-testid="total-chantier"
          style={{
            ...carte,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 8,
            fontSize: 13,
          }}
        >
          <span translate="no">
            {tr('Chantier')} <b>{chantiers.find(([k]) => k === chantier)?.[1]}</b>
          </span>
          <b style={{ whiteSpace: 'nowrap' }}>
            {trf('Total commandé : {0} {1}', devise, fmt(totalChantier))}
          </b>
        </div>
      )}
    </section>
  );
}
